import { Schema } from "effect"
import { Rpc, RpcGroup } from "effect/rpc"
import { MergeReport, PrSnapshot, SignedMergeOrder, WatchedPr } from "../github"
import {
  ArtifactId,
  Epoch,
  Millis,
  NonNegInt,
  OfferId,
  PosInt,
  ProfileId,
  ProjectId,
  RepoId,
  RunId,
  RunnerId,
  Sha256,
} from "../ids"
import { SignedJob } from "../job"
import { Placement } from "../placement"
import { Lease, LeaseRef, RunError, RunHead, RunKind, RunPhase, SessionRef, StopReason, Usage } from "../run"
import { ProfileCliSettings, ProfileCliWish, RunnerManifest } from "../runner"
import { RunFunding } from "../usage"

// #region hub-rpc
export class RunnerRevoked extends Schema.TaggedError<RunnerRevoked>()("RunnerRevoked", {}) {}
export class VersionTooOld extends Schema.TaggedError<VersionTooOld>()("VersionTooOld", {
  minimum: Schema.String,
}) {}
export class ManifestRejected extends Schema.TaggedError<ManifestRejected>()("ManifestRejected", {
  why: Schema.String,
}) {}
export class StaleIncarnation extends Schema.TaggedError<StaleIncarnation>()("StaleIncarnation", {
  current: NonNegInt,
}) {}
export class OfferExpired extends Schema.TaggedError<OfferExpired>()("OfferExpired", { offerId: OfferId }) {}
export class NotEligible extends Schema.TaggedError<NotEligible>()("NotEligible", { why: Schema.String }) {}
/** The fencing error. The runner MUST stop the local process for that run when it sees it. */
export class StaleEpoch extends Schema.TaggedError<StaleEpoch>()("StaleEpoch", {
  runId: RunId,
  current: Epoch,
}) {}
export class LeaseExpired extends Schema.TaggedError<LeaseExpired>()("LeaseExpired", { runId: RunId }) {}

/** RunnerManifest.versions.protocol of runners on `RunnerHubRpcs` (runner ≥ 0.2.0). Below it: the legacy JSON socket. */
export const RUNNER_PROTOCOL_VERSION = 1

/**
 * The Register reply's `cloudFeatures`: `item_model` (decision 0039); `pr_watch` (decision 0059): the cloud handles
 * `ReportPrs`, so a runner with the feature watches and reports.
 *
 * `usage_v2` (decision 0062) says the cloud decodes `RunnerProfile.auth: "unknown"`; without it a runner folds unknown
 * into "subscription". Announced since sim fixes 2b: every cloud we would roll back to is post-F and decodes "unknown",
 * so what runners store in the hub stays readable after a rollback (`docs/deploy.md`).
 *
 * `agent_antigravity` (decision 0109): the cloud decodes `AgentKind: "antigravity"` (profiles, ceiling) and
 * `ModelProvider: "google"` (usage rows); a runner lists Antigravity profiles only to such a cloud.
 *
 * `agent_muse` (decision 0110): the cloud decodes the `muse` agent kind (profiles, ceiling, session refs, usage rows
 * with provider `meta`); a runner lists Muse Code profiles only to such a cloud.
 *
 * `owner_grants` (decision 0138): the cloud decodes `mcpTool` allow rules on `permission.request.options.allow`; a
 * runner offers them on a card only to such a cloud.
 */
export const CLOUD_FEATURES = [
  "item_model",
  "pr_watch",
  "usage_v2",
  "agent_antigravity",
  "agent_muse",
  "owner_grants",
  /** Decision 0143: `POST /api/runners/telemetry` takes a runner's `RunnerTelemetryReport`. */
  "runner_telemetry",
  /**
   * Decision 0149: the hub reads heartbeat `load` and Final `resources` (sanitized; never a reason to refuse either). A
   * runner with the `resources` feature sends them only to a cloud listing this.
   */
  "resources",
] as const

/** One profile wish of the owner-set console config (decision 0007): null = leave as configured on the runner. */
export const ConsoleProfileWish = Schema.Struct({
  id: ProfileId,
  enabled: Schema.NullOr(Schema.Boolean),
  capacity: Schema.NullOr(NonNegInt), // 0..16, checked by the hub route
  /** The profile's CLI permission/sandbox settings (rev 2.1). Absent = leave all as configured; see ProfileCliWish. */
  cli: Schema.optionalKey(ProfileCliWish),
})

/** What the runner applied for one profile, echoed in ConfigApplied. `cli` absent = a runner that predates it. */
export const AppliedProfileConfig = Schema.Struct({
  id: ProfileId,
  enabled: Schema.Boolean,
  capacity: NonNegInt,
  /** The configured CLI settings after the wish: `accessMode`, `mcp` and the overrides (null = from the access mode;
   * `effectiveCli` gives what runs pass). */
  cli: Schema.optionalKey(ProfileCliSettings),
})

export const OfferSummary = Schema.Struct({
  offerId: OfferId,
  runId: RunId,
  kind: RunKind,
  projectId: Schema.NullOr(ProjectId),
  profileId: ProfileId, // the hub picks the profile; the runner may decline
  placement: Placement, // so the runner can re-check its ceiling before claiming
  expiresAt: Millis, // 10 s
})

/** Server → runner pushes, down the one long-lived streaming RPC the runner opened (it always initiates). */
export const HubPush = Schema.TaggedUnion({
  Offer: { offer: OfferSummary },
  OfferWithdrawn: { offerId: OfferId },
  /** Lease revoked (item cancelled, owner stop, reassignment, runner paused, runner outdated). Runner interrupts, then reports Final{interrupted} (also before Phase{running}; a Release would requeue the run). */
  Revoke: {
    lease: LeaseRef,
    reason: Schema.Literals(["cancelled", "owner_stop", "reassigned", "runner_paused"]),
  },
  Admin: { command: Schema.Literals(["pause", "resume", "drain", "revoke", "update_available"]) },
  SignerKeys: {
    keys: Schema.Array(Schema.Struct({ kid: Schema.String, publicKey: Schema.String, notAfter: Millis })),
  },
  /** Owner-set desired config (0007). `rev` strictly increases per runner; echoed in ConfigApplied. */
  Config: {
    rev: NonNegInt,
    capacity: Schema.NullOr(PosInt),
    autoUpdate: Schema.NullOr(Schema.Boolean),
    profiles: Schema.Array(ConsoleProfileWish),
  },
  /** Re-run probes now, then UpdateManifest. */
  Refresh: {},
  /** Owner-requested stock CLI authorization, only for runners with subscription_connect. */
  SetupClaudeSubscription: { connectionId: Schema.String, expiresAt: Millis },
  /** Install this version when idle; null = latest (0010). */
  Update: { version: Schema.NullOr(Schema.String) },
  /** The workspace's registered repos. The runner maps local clones by normalized remote and advertises repoIds. */
  Repos: {
    repos: Schema.Array(
      Schema.Struct({ repoId: RepoId, remote: Schema.String, defaultBranch: Schema.String }),
    ),
  },
  /**
   * Decision 0059: the full list of PRs this runner watches (it is their primary watcher); a higher `rev` replaces it.
   * Only sent to a runner whose manifest lists `pr_watch` (an older runner's Subscribe decode fails on an unknown tag).
   */
  WatchPrs: { rev: NonNegInt, prs: Schema.Array(WatchedPr).check(Schema.isMaxLength(500)) },
  /** Decision 0059: a signed merge order (only to a runner with `pr_watch`). */
  Merge: { order: SignedMergeOrder },
  /**
   * Wave E (decision 0063): take this new runner token (write it 0600), then answer `RekeyApplied`. The hub stores the
   * new hash only after `ok: true`, then closes the runner's sockets so it reconnects with the new key. Only sent to a
   * runner whose manifest lists `rekey`. The token is a secret: never logged.
   */
  Rekey: { token: Schema.String },
})

/** Control-plane facts. Milestones reach the Project DO; token streams never travel here. */
export const ReportBody = Schema.TaggedUnion({
  Phase: { phase: RunPhase }, // "running" = the process started: from now on a lost lease ends the run
  Milestone: {
    kind: Schema.Literals([
      "pr_opened",
      "tests_passed",
      "tests_failed",
      "checkpoint",
      "awaiting_permission",
      "note",
    ]),
    text: Schema.String,
    ref: Schema.NullOr(Schema.String),
  },
  ArtifactAdded: { artifactId: ArtifactId },
  /** Mechanical facts only. The agent's outcome claim arrives through the MCP endpoint (report_outcome), not here. */
  Final: {
    stopReason: StopReason,
    usage: Schema.Array(Usage),
    sessionRef: Schema.NullOr(SessionRef),
    error: Schema.NullOr(RunError),
    streamLastFrameSeq: NonNegInt, // the sink must have acked this before Final is accepted
    /** Worker runs on runner ≥ 0.7.0 (decision 0059); an older cloud's decoder drops the unknown key. */
    head: Schema.optionalKey(RunHead),
    /**
     * Wave F (decision 0062): how the run was paid, as the runner read it from its CLI. The hub adds the owner from
     * its RunnerRecord, never from the runner. An older cloud's decoder drops the unknown key.
     */
    funding: Schema.optionalKey(RunFunding),
    /**
     * Decision 0149: the run's resources (`RunResources`), read with `sanitizeRunResources`. Plain JSON on the wire so a
     * malformed value never fails the Final; an older cloud's decoder drops the key.
     */
    resources: Schema.optionalKey(Schema.Json),
  },
})

export const ActiveLease = Schema.Struct({
  lease: LeaseRef,
  processAlive: Schema.Boolean,
  streamLastFrameSeq: NonNegInt,
})

/** Effect RPC over the runner's control socket to its Workspace DO. Auth = runner token on the upgrade. */
export class RunnerHubRpcs extends RpcGroup.make(
  /** Starts a new incarnation; returns keep/abort verdicts for leases the runner remembers across a restart. */
  Rpc.make("Register", {
    payload: { manifest: RunnerManifest, active: Schema.Array(ActiveLease) },
    success: Schema.Struct({
      runnerId: RunnerId,
      incarnation: NonNegInt,
      serverTime: Millis,
      timing: Schema.Struct({
        leaseTtlMs: PosInt,
        renewEveryMs: PosInt,
        heartbeatEveryMs: PosInt,
        offerTtlMs: PosInt,
      }),
      verdicts: Schema.Array(
        Schema.Struct({
          lease: LeaseRef,
          verdict: Schema.Literals(["keep", "abort"]),
          expiresAt: Schema.NullOr(Millis),
        }),
      ),
      /**
       * What this cloud understands (decision 0039): `["item_model"]` from the item-model cloud. A runner reports
       * non-`tsk_` worktrees only when it holds `item_model`. Absent from older clouds; older runners ignore it
       * (Effect's default excess-property handling strips unknown keys).
       */
      cloudFeatures: Schema.optionalKey(Schema.Array(Schema.String)),
    }),
    error: Schema.Union([RunnerRevoked, VersionTooOld, ManifestRejected]),
  }),
  Rpc.make("UpdateManifest", {
    payload: { incarnation: NonNegInt, manifest: RunnerManifest },
    error: Schema.Union([ManifestRejected, StaleIncarnation]),
  }),
  /** Every 10 s: liveness and capacity. Never extends a lease. */
  Rpc.make("Heartbeat", {
    payload: {
      incarnation: NonNegInt,
      freeSlots: NonNegInt,
      manifestRevision: NonNegInt,
      busyProfiles: Schema.Array(Schema.Struct({ profileId: ProfileId, running: NonNegInt })),
      /**
       * Decision 0149: the machine's load now (`RunnerLoad`), read with `sanitizeRunnerLoad`. Plain JSON on the wire so a
       * malformed or newer value never fails the heartbeat; an older cloud's decoder drops the key.
       */
      load: Schema.optionalKey(Schema.Json),
    },
    success: Schema.Struct({ serverTime: Millis, wantManifest: Schema.Boolean }),
    error: StaleIncarnation,
  }),
  Rpc.make("Subscribe", { payload: { incarnation: NonNegInt }, success: HubPush, stream: true }),
  /** The commit point: epoch + 1, lease (60 s), exclusive keys, MCP and stream tokens, signed job — one transaction. */
  Rpc.make("Claim", {
    payload: { incarnation: NonNegInt, offerId: OfferId, runId: RunId },
    success: Schema.Struct({ lease: Lease, job: SignedJob }),
    error: Schema.Union([OfferExpired, NotEligible, StaleIncarnation]),
  }),
  Rpc.make("Decline", {
    payload: {
      offerId: OfferId,
      reason: Schema.Literals(["busy", "ceiling", "profile_not_ready", "exclusive_local", "shutting_down"]),
      detail: Schema.NullOr(Schema.String),
    },
  }),
  /** Every 20 s (TTL/3), batched. Lost = expired | stale_epoch | revoked: stop that process now. */
  Rpc.make("Renew", {
    payload: { leases: Schema.Array(LeaseRef) },
    success: Schema.Struct({
      serverTime: Millis,
      results: Schema.Array(
        Schema.Struct({
          lease: LeaseRef,
          result: Schema.TaggedUnion({
            renewed: {
              expiresAt: Millis,
              mcpTokens: Schema.Array(
                Schema.Struct({
                  name: Schema.String,
                  token: Schema.Redacted(Schema.String),
                  expiresAt: Millis,
                }),
              ),
              /** The re-minted stream token (W8); absent from hubs before protocol 1. */
              streamToken: Schema.optionalKey(Schema.Redacted(Schema.String)),
            },
            lost: { reason: Schema.Literals(["expired", "stale_epoch", "revoked"]) },
          }),
        }),
      ),
    }),
  }),
  /** Ends a lease WITHOUT an attempt (ceiling refusal after claim, prepare failed, shutdown before start). Only
   * valid while phase = preparing; the run is offered again with the next epoch, up to MAX_PRESTART_RELEASES. */
  Rpc.make("Release", {
    payload: {
      lease: LeaseRef,
      reason: Schema.Literals(["ceiling", "prepare_failed", "shutdown"]),
      detail: Schema.NullOr(Schema.String),
    },
    error: StaleEpoch,
  }),
  /** Idempotent on (runId, epoch, seq). Final releases the lease once accepted. */
  Rpc.make("Report", {
    payload: { lease: LeaseRef, seq: PosInt, at: Millis, body: ReportBody },
    error: Schema.Union([StaleEpoch, LeaseExpired]),
  }),
  /** Console (0007): the runner applied (or refused) the desired config `rev`. */
  Rpc.make("ConfigApplied", {
    payload: {
      incarnation: NonNegInt,
      rev: NonNegInt,
      ok: Schema.Boolean,
      capacity: PosInt,
      autoUpdate: Schema.Boolean,
      profiles: Schema.Array(AppliedProfileConfig),
      error: Schema.NullOr(Schema.String),
    },
    error: StaleIncarnation,
  }),
  /** Console (0010): progress of a requested self-update. */
  Rpc.make("UpdateStatus", {
    payload: {
      incarnation: NonNegInt,
      state: Schema.Literals([
        "waiting_idle",
        "downloading",
        "installed",
        "up_to_date",
        "failed",
        "unsupported",
      ]),
      version: Schema.NullOr(Schema.String),
      message: Schema.NullOr(Schema.String),
    },
    error: StaleIncarnation,
  }),
  /**
   * Decision 0059: the PR watch's changed snapshots and merge results. A runner calls it only when its Register reply's
   * `cloudFeatures` has `pr_watch`. `unknown` = artifacts not on this runner's watch list (drop them).
   */
  Rpc.make("ReportPrs", {
    payload: {
      incarnation: NonNegInt,
      snapshots: Schema.Array(PrSnapshot).check(Schema.isMaxLength(50)),
      merges: Schema.Array(MergeReport).check(Schema.isMaxLength(10)),
    },
    success: Schema.Struct({ accepted: NonNegInt, unknown: Schema.Array(ArtifactId) }),
    error: StaleIncarnation,
  }),
  /** Wave E (decision 0063): the runner stored (or failed to store) the token of `HubPush.Rekey`. */
  Rpc.make("RekeyApplied", {
    payload: {
      incarnation: NonNegInt,
      ok: Schema.Boolean,
      error: Schema.optionalKey(Schema.String),
    },
    error: StaleIncarnation,
  }),
  /** Pre-signed R2 upload for an artifact or a large pack fetch, authorised by the run's lease. Not served this wave. */
  Rpc.make("PresignUpload", {
    payload: { lease: LeaseRef, sha256: Sha256, bytes: NonNegInt, contentType: Schema.String },
    success: Schema.Struct({ url: Schema.String, key: Schema.String }),
    error: StaleEpoch,
  }),
) {}
// #endregion hub-rpc
